/**
 * MODULE: AssistantPageLanding.tsx
 * PURPOSE: Perplexity-style empty state — centered brand + bottom-anchored composer.
 * RECENT: 2026-07-03 [2.2.1477] — Proactive page landing: scrollable advisor welcome + pinned **`assistant-page-composer-dock`** (no outer ring or divider).
 * RECENT_PRIOR: 2026-07-03 [2.2.1476] — Proactive advisor welcome keeps composer pinned at bottom (scrollable insights above).
 * RECENT_PRIOR: 2026-06-18 [2.2.1450] — Shared by full-page **`/langflow-assistant`** and rail idle layout; **`assistantPageTheme`** tokens.
 */

import { SparklesIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { BRAZN_APP_ICON_SRC } from '../../../components/auth/AuthBrandMark';
import type { AssistantWelcomeChip } from '../../../components/assistant/assistantWelcomePrompts';
import { AssistantWelcomeChipButton } from '../../../components/assistant/AssistantWelcomeChipButton';
import { ASSISTANT_PAGE_BG_CLASS, ASSISTANT_RAIL_BG_CLASS } from './assistantPageTheme';

export type AssistantPageLandingProps = {
  composer: ReactNode;
  welcomeChips: AssistantWelcomeChip[];
  onWelcomeChipClick: (chip: AssistantWelcomeChip) => void;
  chipsDisabled?: boolean;
  chipsLoading?: boolean;
  flowResolving?: boolean;
  quotaChip?: ReactNode;
  proactiveWelcome?: ReactNode;
  /** Narrow right rail — smaller logo and title. */
  compact?: boolean;
  /** Dark Perplexity-style layout — logo centered, composer pinned to bottom. */
  immersive?: boolean;
};

export function AssistantPageLanding({
  composer,
  welcomeChips,
  onWelcomeChipClick,
  chipsDisabled = false,
  chipsLoading = false,
  flowResolving = false,
  quotaChip,
  proactiveWelcome,
  compact = false,
  immersive = false,
}: AssistantPageLandingProps) {
  if (proactiveWelcome) {
    return (
      <div
        data-testid="ai-empty-state"
        className={`flex h-full min-h-0 w-full flex-col ${
          immersive ? ASSISTANT_RAIL_BG_CLASS : ASSISTANT_PAGE_BG_CLASS
        }`}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 pt-2">
            {proactiveWelcome}
          </div>
        </div>

        {!flowResolving ? (
          <div
            className="shrink-0 px-3 pb-4 pt-3"
            data-testid="assistant-page-composer-dock"
          >
            <div className={`mx-auto w-full ${compact ? 'max-w-none' : 'max-w-2xl'}`}>
              {composer}
              {(quotaChip || welcomeChips.length > 0) && (
                <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                  {quotaChip}
                  {chipsLoading && welcomeChips.length === 0 ? (
                    <p
                      className="text-xs text-gray-400 dark:text-gray-500"
                      data-testid="assistant-welcome-chips-loading"
                    >
                      Loading personalized suggestions…
                    </p>
                  ) : null}
                  {welcomeChips.map((chip) => (
                    <AssistantWelcomeChipButton
                      key={chip.label}
                      chip={chip}
                      disabled={chipsDisabled}
                      onClick={() => onWelcomeChipClick(chip)}
                      size="sm"
                      className="rounded-full border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  const previewChip = welcomeChips[0];
  const extraChips = welcomeChips.slice(1);

  if (immersive) {
    return (
      <div
        data-testid="ai-empty-state"
        className={`flex h-full min-h-0 w-full flex-col ${ASSISTANT_RAIL_BG_CLASS}`}
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 pb-6">
          <div className={`flex flex-col items-center ${compact ? 'gap-2' : 'gap-3'}`}>
            <img
              src={BRAZN_APP_ICON_SRC}
              alt=""
              className={`rounded-md object-contain brightness-110 ${compact ? 'h-11 w-11' : 'h-14 w-14'}`}
              width={compact ? 44 : 56}
              height={compact ? 44 : 56}
              aria-hidden
            />
            <h1
              className={`font-normal tracking-tight text-gray-700 dark:text-gray-200 ${
                compact ? 'text-xl' : 'text-2xl'
              }`}
            >
              brazn ai assistant
            </h1>
          </div>

          {flowResolving ? (
            <div className="mt-8 flex flex-col items-center gap-3 text-center text-gray-400">
              <SparklesIcon className="h-8 w-8 text-gray-500" aria-hidden />
              <p className="max-w-xs text-sm font-medium text-gray-400">
                Connecting — looking up your tenant&apos;s Langflow flow…
              </p>
            </div>
          ) : null}
        </div>

        {!flowResolving ? (
          <div className="shrink-0 px-3 pb-4 pt-1">
            {previewChip ? (
              <button
                type="button"
                disabled={chipsDisabled}
                onClick={() => onWelcomeChipClick(previewChip)}
                className="mb-3 flex w-full items-start gap-2 rounded-lg px-1 py-1 text-left text-sm text-gray-500 transition hover:text-gray-700 disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200"
                data-testid="assistant-immersive-preview-prompt"
              >
                <SparklesIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden />
                <span className="line-clamp-2">{previewChip.prompt}</span>
              </button>
            ) : chipsLoading ? (
              <p
                className="mb-3 px-1 text-xs text-gray-500"
                data-testid="assistant-welcome-chips-loading"
              >
                Loading personalized suggestions…
              </p>
            ) : null}

            <div className="w-full">{composer}</div>

            {(quotaChip || extraChips.length > 0) && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                {quotaChip}
                {extraChips.map((chip) => (
                  <AssistantWelcomeChipButton
                    key={chip.label}
                    chip={chip}
                    disabled={chipsDisabled}
                    onClick={() => onWelcomeChipClick(chip)}
                    size="sm"
                    className="rounded-full border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
                  />
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      data-testid="ai-empty-state"
      className={`flex w-full flex-col items-center justify-center ${compact ? 'max-w-none gap-5 px-2 py-6' : 'max-w-2xl gap-8 px-4 py-8'}`}
    >
      <div className={`flex flex-col items-center ${compact ? 'gap-2' : 'gap-3'}`}>
        <img
          src={BRAZN_APP_ICON_SRC}
          alt=""
          className={`rounded-md object-contain ${compact ? 'h-10 w-10' : 'h-14 w-14'}`}
          width={compact ? 40 : 56}
          height={compact ? 40 : 56}
          aria-hidden
        />
        <h1
          className={`font-normal tracking-tight text-gray-700 dark:text-gray-200 ${
            compact ? 'text-xl' : 'text-3xl'
          }`}
        >
          brazn ai assistant
        </h1>
      </div>

      {flowResolving ? (
        <div className="flex flex-col items-center gap-3 text-center text-gray-500 dark:text-gray-400">
          <SparklesIcon className="h-10 w-10 text-primary-500/70 dark:text-primary-400/70" aria-hidden />
          <p className="max-w-sm text-sm font-medium text-sky-800 dark:text-sky-200">
            Connecting — looking up your tenant&apos;s Langflow flow…
          </p>
        </div>
      ) : (
        <>
          <div className="w-full">{composer}</div>

          <div className="flex w-full flex-wrap items-center justify-center gap-2">
            {quotaChip}
            {chipsLoading && welcomeChips.length === 0 ? (
              <p
                className="text-xs text-gray-400 dark:text-gray-500"
                data-testid="assistant-welcome-chips-loading"
              >
                Loading personalized suggestions…
              </p>
            ) : null}
            {welcomeChips.map((chip) => (
              <AssistantWelcomeChipButton
                key={chip.label}
                chip={chip}
                disabled={chipsDisabled}
                onClick={() => onWelcomeChipClick(chip)}
                size="sm"
                className="rounded-full border-gray-200 bg-white shadow-sm hover:border-gray-300 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-gray-500 dark:hover:bg-gray-700"
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
